import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  HeartIcon,
  GlobeAltIcon,
  UsersIcon,
  ShieldCheckIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const AboutPage: React.FC = () => {
  const stats = [
    { label: 'Happy Travelers', value: '10,000+', icon: UsersIcon },
    // { label: 'Partner Resorts', value: '50+', icon: GlobeAltIcon },
    { label: 'Years Experience', value: '16+', icon: StarIcon },
    { label: 'Customer Satisfaction', value: '99.99%', icon: HeartIcon }
  ];

  const values = [
    {
      icon: HeartIcon,
      title: 'Passion for Travel',
      description: 'We believe travel transforms lives and creates lasting memories. Every package is designed with love and attention to detail.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Trust & Reliability',
      description: 'Your peace of mind is our priority. We work only with verified partners and provide 24/7 support throughout your journey.'
    },
    {
      icon: GlobeAltIcon,
      title: 'Local Expertise',
      description: 'Our team of local experts knows Bali inside out. We share insider knowledge to make your experience truly authentic.'
    },
    {
      icon: UsersIcon,
      title: 'Customer First',
      description: 'Every decision we make is centered around creating exceptional experiences for our travelers. Your satisfaction is our success.'
    }
  ];

  const team = [
    {
      name: 'Sarah Johnson',
      role: 'Founder & CEO',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b5e5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      bio: 'Former travel executive with 15+ years in luxury hospitality. Passionate about sustainable tourism.'
    },
    {
      name: 'Made Sutrisna',
      role: 'Head of Operations',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      bio: 'Bali native with deep connections to the island\'s best resorts and hidden gems.'
    },
    {
      name: 'Emma Chen',
      role: 'Customer Experience Director',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      bio: 'Travel enthusiast who ensures every customer journey exceeds expectations.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Bali Getaway - Your Trusted Travel Partner</title>
        <meta name="description" content="Learn about Bali Getaway's mission to create unforgettable travel experiences. Meet our team and discover why thousands trust us for their Bali vacations." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>

        <div className="relative z-10 text-center text-white container-max px-4">
          <h1 className="text-5xl md:text-6xl font-bold font-display mb-4">
            About Bali Getaway
          </h1>
          
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold font-display text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-lg text-gray-700">
                <p>
                  Bali, often called the Island of the Gods, is a jewel of the Indonesian archipelago and one of the world’s most beloved travel destinations. From its sun-drenched beaches and lush green terraces to its colorful culture and warm hospitality, Bali captures the hearts of everyone who visits.
                </p>
                <p>
                  At Bali Getaway, we make it simple to plan your perfect island escape. Whether you’re dreaming of a romantic retreat for two, a fun-filled family vacation, or a peaceful break surrounded by nature, you’ll find stays and experiences carefully selected to suit every style and budget.
                </p>
                <p>
                  Our goal is to help you discover the best of Bali — from scenic resorts and vibrant local spots to hidden gems that make your journey truly special.
                </p>
                <p>
                  Start your Bali adventure with us and experience why this island paradise is loved by couples, families, and travelers from around the world.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://res.cloudinary.com/dyecmrs6w/image/upload/v1762773061/about-us_ifylpw.png"
                alt="Bali sunset view"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                16+ Years
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="section-padding bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-display mb-4">
              Our Impact in Numbers
            </h2>
            {/* <p className="text-xl opacity-90 max-w-3xl mx-auto">
              These numbers represent real people who trusted us with their dream vacations
            </p> */}
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-white rounded-full shadow-lg flex items-center justify-center">
                  <stat.icon className="h-8 w-8 text-orange-600" />
                </div>
                <div className="text-4xl font-bold mb-2">{stat.value}</div>
                <div className="text-lg opacity-90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Why Choose Bali Getaway - Modern Redesign */}
      <section className="section-padding bg-gradient-to-br from-orange-50 via-yellow-50 to-orange-100">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black bg-gradient-to-r from-orange-600 via-yellow-600 to-orange-700 bg-clip-text text-transparent mb-6">
              Why Choose Bali Getaway?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Discover what makes us the preferred choice for thousands of travelers seeking authentic Bali experiences
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* Feature 1 */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-yellow-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  <span className="text-2xl font-black text-white">🏆</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors">
                  Handpicked destinations
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Every destination is handpicked by our Bali experts to ensure comfort, fun and authentic cultural experiences that create lasting memories.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-yellow-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  <span className="text-2xl font-black text-white">❤️</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors">
                  Perfect for Everyone
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Whether it's a romantic getaway or a family adventure, our diverse packages cater to every travel style and budget.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="group relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-yellow-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                  <span className="text-2xl font-black text-white">💎</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors">
                  Unbeatable Value
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Exceptional deals across stunning resorts and unique accommodations, giving you Exclusive experiences at the best possible prices.
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="container-max text-center">
          <h2 className="text-4xl font-bold font-display mb-4">
            Ready to Start Your Bali Journey?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied travelers who've discovered the magic of Bali with us.
            Your adventure awaits!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/packages"
              className="bg-white text-blue-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Browse Packages
            </a>
            <a
              href="/contact"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
